import { ColumnDef } from "@tanstack/react-table";
import { EditIcon, TrashAltIcon } from "assets/icons";
import { useLanguage } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  ItemsTable,
  TableItemStatusControl,
  Status,
  TableItemActionsControl
} from "ui";

import { useUpdateDeliveryZoneStatus } from "../../apis";
import {
  DELIVERY_ZONE_STATUSES_LIST,
  DeliveryZoneStatuses
} from "../../constants";
import { DeliveryFeeListItem } from "../../financial.types";
import { PaymentMethodActivationMark } from "./atoms";
import { DeliveryFeesTableProps } from "./DeliveryFeesTable.types";

const DeliveryFeesTable = ({
  items,
  sorting,
  isLoading,
  refetchList,
  setSorting,
  onEditFeePress,
  onDeleteFeePress
}: DeliveryFeesTableProps) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const updateManagerStatus = useUpdateDeliveryZoneStatus({
    onSuccess: () => {
      toast.success(t("Delivery Zone status updated successfully"));
      setOpenedItemStatusesIndex(null);
      refetchList();
    }
  });

  const [openedItemStatusesIndex, setOpenedItemStatusesIndex] = useState<
    number | null
  >(null);
  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const handleManagerStatusUpdate = (isActive: boolean) => {
    if (openedItemStatusesIndex !== null) {
      updateManagerStatus.mutate({
        zoneId: items[openedItemStatusesIndex].deliveryZoneId,
        isActive
      });
    }
  };

  const statusesList: Status[] = DELIVERY_ZONE_STATUSES_LIST.map(
    (orStatus) => ({
      ...orStatus,
      onClick: () =>
        handleManagerStatusUpdate(
          orStatus.value === DeliveryZoneStatuses.Active
        )
    })
  );

  const columns: Array<ColumnDef<DeliveryFeeListItem, DeliveryFeeListItem[]>> =
    useMemo(
      () => [
        {
          id: "Country",
          accessorKey: "Country",
          header: t("Country"),
          size: 15,
          minSize: 15,
          cell: ({ row }) => items[row.index].deliveryZone.country
        },
        {
          id: "City",
          accessorKey: "City",
          header: t("City"),
          size: 15,
          minSize: 15,
          cell: ({ row }) => items[row.index].deliveryZone.city
        },
        {
          id: "Neighborhood",
          accessorKey: "neighborhood",
          header: t("Area"),
          size: 15,
          minSize: 15,
          cell: ({ row }) => items[row.index].deliveryZone.neighborhood
        },

        {
          id: "isExpedition",
          accessorKey: "isExpedition",
          header: t("Type"),
          size: 8,
          minSize: 8,
          cell: ({ row }) =>
            items[row.index].deliveryZone.isExpedition
              ? t("Expedition")
              : t("Abidjan")
        },

        {
          id: "SizeSFee",
          accessorKey: "sizeSFee",
          header: t("Size S Fee"),
          size: 12,
          minSize: 12,
          cell: ({ row }) => items[row.index].sizeSFee.toLocaleString("en-US")
        },

        {
          id: "SizeMFee",
          accessorKey: "SizeMFee",
          header: t("Size M Fee"),
          size: 12,
          minSize: 12,
          cell: ({ row }) => items[row.index].sizeMFee.toLocaleString("en-US")
        },

        {
          id: "SizeLFee",
          accessorKey: "SizeLFee",
          header: t("Size L Fee"),
          size: 12,
          minSize: 12,
          cell: ({ row }) => items[row.index].sizeLFee.toLocaleString("en-US")
        },

        {
          header: "COD/MM",
          size: 5,
          minSize: 5,
          cell: ({ row }) => (
            <PaymentMethodActivationMark
              isActiveCOD={items[row.index].allowCashOnDelivery}
              isActiveMM={items[row.index].allowMobileMoney}
            />
          )
        },
        {
          header: t("Status"),
          enableSorting: false,
          size: 11,
          minSize: 11,
          cell: ({ row }) => (
            <TableItemStatusControl
              rowIndex={row.index}
              activeStatusIndex={items[row.index].deliveryZone.isActive ? 0 : 1}
              openedItemStatusesIndex={openedItemStatusesIndex}
              setOpenedItemStatusesIndex={setOpenedItemStatusesIndex}
              statusesList={statusesList}
            />
          )
        },
        {
          accessorKey: "dotsColumn",
          header: () => null,
          size: 4,
          minSize: 4,
          cell: ({ row }) => (
            <TableItemActionsControl
              actionsList={[
                {
                  label: "Edit",
                  Icon: <EditIcon fill={"#090914"} width={18} height={18} />,
                  onClick: () => onEditFeePress(items[row.index])
                },
                {
                  label: "Delete",
                  Icon: (
                    <TrashAltIcon fill={"#ff0033"} width={18} height={18} />
                  ),
                  labelColor: "#ff0033",
                  onClick: () => onDeleteFeePress(items[row.index])
                }
              ]}
              rowIndex={row.index}
              setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
              openedItemOptionsIndex={openedItemOptionsIndex}
            />
          ),
          enableSorting: false
        }
      ],
      [items, openedItemStatusesIndex, openedItemOptionsIndex, currentLanguage]
    );

  return (
    <ItemsTable
      columns={columns}
      data={items}
      sorting={sorting}
      skeletonHeight={65}
      setSorting={setSorting}
      isLoading={isLoading}
    />
  );
};

export default DeliveryFeesTable;
